import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Flex, Form, InputNumber, Modal, ModalProps, Popconfirm, Select, Spin, message } from 'antd';
import { EProductType } from '@leedpulse-monorepo/enums';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import debounce from 'lodash/debounce';
import UserDoc from '../../../../domain/models/UserDoc';
import CompaniesContext from '../../../contexts/data/companies/CompaniesContext';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import addSubscriptionToCompany from '../../../../domain/use_cases/companies/addSubscriptionToCompany';
import getCompanyStripeInfo, { ICompanyStripeInfo } from '../../../../domain/use_cases/companies/getCompanyStripeInfo';
import getPlansRefs from '../../../../domain/use_cases/plans/getPlansRefs';
import DebouncedPlansSelect from '../../plans_page/select/DebouncedPlansSelect';

interface CompanyAddSubscriptionModalProps extends ModalProps {
    onSuccess?: (createdSubscriptions: string[]) => void | Promise<void>;
}

interface PlanLine {
    plan_id?: string;
    quantity?: number;
}

interface AddSubscriptionFormValues {
    lines?: PlanLine[];
}

const initialValues: AddSubscriptionFormValues = {
    lines: [{ plan_id: undefined, quantity: 1 }],
};

const USERS_PAGE_SIZE = 20;

const userLabel = (user: UserDoc) => {
    const fullname = `${user.firstname} ${user.lastname}`.trim();
    return fullname ? `${fullname} - ${user.email}` : user.email;
};

const formatDate = (unixSeconds: number) =>
    new Date(unixSeconds * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const formatAmount = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);

export default function CompanyAddSubscriptionModal({
    onSuccess,
    ...modalProps
}: CompanyAddSubscriptionModalProps) {
    const companiesContext = useContext(CompaniesContext);
    const authContext = useContext(AuthenticationContext);

    const companyDoc = companiesContext.selected as ICompany;

    const [form] = Form.useForm<AddSubscriptionFormValues>();
    const [stripeInfo, setStripeInfo] = useState<ICompanyStripeInfo | null>(null);
    const [planPriceById, setPlanPriceById] = useState<Record<string, number>>({});
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [assignedUser, setAssignedUser] = useState<UserDoc | null>(null);
    const [userSearch, setUserSearch] = useState('');
    const [visibleUsersCount, setVisibleUsersCount] = useState(USERS_PAGE_SIZE);

    const watchedLines = Form.useWatch('lines', form) as PlanLine[] | undefined;
    const totalNumbers = (watchedLines ?? []).reduce((acc, l) => acc + (l?.plan_id ? Number(l?.quantity) || 0 : 0), 0);

    // Monthly amount of the order — drives the billing confirmation before ordering.
    const monthlyTotal = (watchedLines ?? []).reduce(
        (acc, l) => acc + (l?.plan_id ? (planPriceById[l.plan_id] ?? 0) * (Number(l?.quantity) || 0) : 0),
        0
    );

    // Users of the current company only — already loaded by CompanyDetailPage, no extra fetch.
    const companyUsers = companiesContext.companyUsers;

    const filteredUsers = useMemo(() => {
        const query = userSearch.trim().toLowerCase();
        if (!query) return companyUsers;
        return companyUsers.filter((user) => userLabel(user).toLowerCase().includes(query));
    }, [companyUsers, userSearch]);

    const userOptions = useMemo(
        () => filteredUsers.slice(0, visibleUsersCount).map((user) => ({ value: user.uid, label: userLabel(user) })),
        [filteredUsers, visibleUsersCount]
    );

    const debouncedUserSearch = useMemo(() => debounce(setUserSearch, 300), []);

    const canOrder = Boolean(companyDoc?.id_stripe) && stripeInfo?.has_valid_payment === true;

    useEffect(() => {
        if (!modalProps.open || !authContext.authentication || !companyDoc?.id) return;

        // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
        // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
        // external system") ; le setState synchrone est le reset qui precede l'appel.
        // eslint-disable-next-line react/set-state-in-effect
        setAssignedUser(null);
        setUserSearch('');
        setVisibleUsersCount(USERS_PAGE_SIZE);
        form.resetFields();
        setIsLoadingInfo(true);
        Promise.all([
            getCompanyStripeInfo(companyDoc.id, authContext.authentication),
            getPlansRefs({ product: EProductType.LEEDFLOW }),
        ])
            .then(([info, { plansRefs }]) => {
                setStripeInfo(info);
                setPlanPriceById(Object.fromEntries(plansRefs.map((plan) => [plan.id, plan.price?.value ?? 0])));
            })
            .catch((e) => {
                console.error('[CompanyAddSubscriptionModal] init error:', e);
                setStripeInfo(null);
            })
            .finally(() => setIsLoadingInfo(false));
    }, [authContext.authentication, companyDoc?.id, form, modalProps.open]);

    const handleSubmit = async () => {
        if (!authContext.authentication) return;

        const values = await form.validateFields();

        // The backend orders one number per item, so a line of quantity N is expanded into N items.
        const items = (values.lines ?? [])
            .filter((l) => l?.plan_id && Number(l?.quantity) > 0)
            .flatMap((l) => Array.from({ length: Number(l.quantity) }, () => ({ plan_id: l.plan_id as string })));

        setIsSubmitting(true);
        try {
            const { created_subscriptions } = await addSubscriptionToCompany(
                {
                    company_id: companyDoc.id,
                    items,
                    ...(assignedUser ? { user_uid: assignedUser.uid, user_phone: assignedUser.phone } : {}),
                },
                authContext.authentication
            );
            message.success(`${created_subscriptions.length} abonnement(s) créé(s)`);
            await onSuccess?.(created_subscriptions);
        } catch (e: unknown) {
            console.error('[CompanyAddSubscriptionModal] addSubscriptionToCompany error:', e);
            message.error(e instanceof Error ? e.message : "Erreur lors de la création de l'abonnement");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            title={`Ajouter un abonnement à ${companyDoc?.name ?? ''}`}
            open={modalProps.open}
            onCancel={modalProps.onCancel}
            width={modalProps.width}
            destroyOnHidden
            footer={
                <Flex justify="flex-end" gap={8}>
                    <Button onClick={modalProps.onCancel}>{modalProps.cancelText}</Button>
                    {monthlyTotal > 0 ? (
                        <Popconfirm
                            title="Cette commande facture le client"
                            description={
                                <span>
                                    {formatAmount(monthlyTotal)} / mois pour {totalNumbers} numéro(s).<br />
                                    La carte enregistrée sera débitée immédiatement, au prorata de la période en cours.
                                </span>
                            }
                            okText="Confirmer la commande"
                            cancelText="Annuler"
                            onConfirm={() => void handleSubmit()}
                        >
                            <Button type="primary" loading={isSubmitting} disabled={!canOrder || totalNumbers === 0}>
                                {modalProps.okText}
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Button
                            type="primary"
                            loading={isSubmitting}
                            disabled={!canOrder || totalNumbers === 0}
                            onClick={() => void handleSubmit()}
                        >
                            {modalProps.okText}
                        </Button>
                    )}
                </Flex>
            }
        >
            {isLoadingInfo ? (
                <Flex justify="center" align="center" style={{ padding: '1rem' }}>
                    <Spin />
                </Flex>
            ) : (
                <Flex vertical gap={12}>
                    {!companyDoc?.id_stripe ? (
                        <Alert
                            type="error"
                            showIcon
                            message="Aucun client Stripe"
                            description="Cette entreprise n'est reliée à aucun client Stripe. Reliez-la depuis l'onglet Détails avant de commander."
                        />
                    ) : !stripeInfo?.has_valid_payment ? (
                        <Alert
                            type="error"
                            showIcon
                            message="Aucun moyen de paiement"
                            description="Le client Stripe de cette entreprise n'a aucune carte enregistrée. La commande serait refusée."
                        />
                    ) : (
                        <Alert
                            type="warning"
                            showIcon
                            message="La carte enregistrée sera débitée immédiatement"
                            description={
                                stripeInfo.has_subscription && stripeInfo.current_period_end
                                    ? `L'abonnement Stripe existant sera complété au prorata jusqu'à la prochaine échéance du ${formatDate(stripeInfo.current_period_end)}.`
                                    : "Aucun abonnement Stripe actif : un nouvel abonnement sera créé et facturé immédiatement."
                            }
                        />
                    )}

                    <Form layout="vertical" form={form} initialValues={initialValues} disabled={!canOrder}>
                        <Form.List
                            name="lines"
                            rules={[
                                {
                                    validator: async (_, lines: PlanLine[]) => {
                                        const planIds = (lines ?? []).map((l) => l?.plan_id).filter(Boolean);
                                        if (new Set(planIds).size !== planIds.length) {
                                            return Promise.reject(new Error('Un même plan ne peut être ajouté que sur une seule ligne.'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <React.Fragment key={field.key}>
                                            {index > 0 && <Divider size="small" />}
                                            <Flex gap={8} align="flex-start">
                                                <Form.Item
                                                    label="Plan"
                                                    name={[field.name, 'plan_id']}
                                                    rules={[{ required: true, message: 'Veuillez sélectionner un plan' }]}
                                                    style={{ margin: 0, flex: 3, minWidth: 0 }}
                                                >
                                                    <DebouncedPlansSelect
                                                        auth={authContext.authentication}
                                                        product={EProductType.LEEDFLOW}
                                                        showSearch
                                                        customOptions={false}
                                                        placeholder="Rechercher un plan..."
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Numéros"
                                                    name={[field.name, 'quantity']}
                                                    rules={[{ required: true, message: 'Veuillez entrer une quantité' }]}
                                                    style={{ margin: 0, flex: 1 }}
                                                >
                                                    <InputNumber min={1} max={99} precision={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                                {fields.length > 1 && (
                                                    <Form.Item label=" " style={{ margin: 0 }}>
                                                        <MinusCircleOutlined
                                                            onClick={() => remove(field.name)}
                                                            style={{ color: 'var(--danger-color)' }}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </Flex>
                                        </React.Fragment>
                                    ))}

                                    <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={() => add({ quantity: 1 })}
                                        style={{ width: '100%', marginTop: '1rem' }}
                                    >
                                        Ajouter un plan
                                    </Button>

                                    <Form.ErrorList errors={errors} />
                                </>
                            )}
                        </Form.List>

                        <Divider />

                        <Form.Item
                            label="Assigner à un utilisateur"
                            help="Optionnel. Sans utilisateur, le superviseur de l'entreprise est assigné automatiquement s'il est le seul et n'a pas encore de numéro."
                        >
                            <Select
                                showSearch
                                allowClear
                                filterOption={false}
                                placeholder="Rechercher un utilisateur..."
                                options={userOptions}
                                value={assignedUser?.uid}
                                onSearch={(query) => {
                                    setVisibleUsersCount(USERS_PAGE_SIZE);
                                    debouncedUserSearch(query);
                                }}
                                onChange={(uid) => setAssignedUser(companyUsers.find((user) => user.uid === uid) ?? null)}
                                onPopupScroll={(e) => {
                                    const target = e.currentTarget;
                                    if (
                                        target.scrollTop + target.offsetHeight >= target.scrollHeight - 8 &&
                                        visibleUsersCount < filteredUsers.length
                                    ) {
                                        setVisibleUsersCount((count) => count + USERS_PAGE_SIZE);
                                    }
                                }}
                                notFoundContent="Aucun utilisateur dans cette entreprise"
                            />
                        </Form.Item>
                    </Form>
                </Flex>
            )}
        </Modal>
    );
}
